import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Command } from 'lucide-react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { commands } from './commandPaletteConfig';

export const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    let spaceDownTime = null;

    const handleKeyDown = (e) => {
      if (e.code === 'Space' && spaceDownTime === null) {
        spaceDownTime = Date.now();
      }
    };

    const handleKeyUp = (e) => {
      if (e.code === 'Space' && spaceDownTime) {
        const heldTime = Date.now() - spaceDownTime;
        if (heldTime > 500) {
          e.preventDefault();
          setOpen(true);
        }
        spaceDownTime = null;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const handleClose = () => {
    setQuery('');
    setOpen(false);
  };

  const groupedCommands = commands.reduce((acc, cmd) => {
    if (!acc[cmd.group]) acc[cmd.group] = [];
    acc[cmd.group].push(cmd);
    return acc;
  }, {});

  const handleCommandAction = (cmd) => {
    if (cmd.type === 'link') {
      navigate(cmd.to);
    } else if (cmd.type === 'external') {
      window.open(cmd.href, '_blank');
    } else if (cmd.type === 'action') {
      cmd.onClick?.();
    }
    handleClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />

          <motion.div
            className="fixed top-1/4 transform -translate-x-1/2 z-[100] w-full max-w-full"
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <div className="rounded-2xl mx-auto max-w-2xl bg-[linear-gradient(to_bottom,_#2F6ACB,_#4455D0,_#4B3CBF)] dark:bg-[linear-gradient(to_bottom,_#0F2027,_#203A43,_#2C5364)] ring-1 ring-white/20 border border-white/10 shadow-xl">
              <div className="p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <Command size={16} className="text-white/50" />
                  <input
                    ref={inputRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search or type command..."
                    className="w-full text-sm bg-transparent text-white placeholder-white/70 focus:outline-none"
                  />
                </div>

                <div className="max-h-64 overflow-y-auto space-y-4">
                  {Object.entries(groupedCommands).map(([group, cmds]) => {
                    const filtered = cmds.filter((cmd) =>
                      cmd.name.toLowerCase().includes(query.toLowerCase())
                    );
                    if (filtered.length === 0) return null;

                    return (
                      <div key={group}>
                        <p className="text-xs text-white/60 uppercase mb-2 px-1">
                          {group}
                        </p>
                        <div className="flex flex-col space-y-1">
                          {filtered.map((cmd) => (
                            <button
                              key={cmd.name}
                              onClick={() => handleCommandAction(cmd)}
                              className="w-full text-left px-3 py-2 rounded-md text-sm text-white/90 hover:bg-white/10 transition"
                            >
                              {cmd.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
